/* eslint-disable */
// Small markdown renderer for Claude's replies: headings, bold/italic, inline code, code blocks
// (with copy), lists, tables, quotes, links. Builds React elements only (no raw HTML), so it is safe.
import { useState } from 'react';
import styles from './ServerChatPage.module.scss';

const INLINE_RE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(\[[^\]]+\]\((https?:\/\/[^)\s]+)\))|(https?:\/\/[^\s<>()]+)/g;

function inline(text, keyBase) {
	const out = [];
	let last = 0;
	let m;
	let i = 0;
	// Own copy per call: bold/italic recurse, and a shared /g regex loops forever and freezes the tab.
	const re = new RegExp(INLINE_RE.source, 'g');
	while ((m = re.exec(text))) {
		if (m.index > last) out.push(text.slice(last, m.index));
		const k = `${keyBase}-${i++}`;
		if (m[1]) out.push(<code key={k} className={styles.mdCode}>{m[1].slice(1, -1)}</code>);
		else if (m[2]) out.push(<strong key={k}>{inline(m[2].slice(2, -2), k)}</strong>);
		else if (m[3]) out.push(<em key={k}>{inline(m[3].slice(1, -1), k)}</em>);
		else if (m[4]) {
			const label = m[4].slice(1, m[4].indexOf(']('));
			out.push(<a key={k} href={m[5]} target='_blank' rel='noopener noreferrer'>{label}</a>);
		} else if (m[6]) out.push(<a key={k} href={m[6]} target='_blank' rel='noopener noreferrer'>{m[6]}</a>);
		last = re.lastIndex;
	}
	if (last < text.length) out.push(text.slice(last));
	return out;
}

function CodeBlock({ lang, code }) {
	const [copied, setCopied] = useState(false);
	const copy = () => {
		navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
	};
	return (
		<div className={styles.mdPre}>
			<div className={styles.mdPreHead}>
				<span>{lang || 'text'}</span>
				<button type='button' onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
			</div>
			<pre><code>{code}</code></pre>
		</div>
	);
}

const splitRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
const isTableSep = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line || '');

export default function MarkdownView({ text }) {
	const lines = (text || '').replace(/\r\n/g, '\n').split('\n');
	const blocks = [];
	let i = 0;
	let k = 0;
	while (i < lines.length) {
		const line = lines[i];
		const key = `b${k++}`;
		const fence = line.match(/^\s*```(\S*)/);
		if (fence) {
			const body = [];
			i += 1;
			while (i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i++]);
			i += 1;
			blocks.push(<CodeBlock key={key} lang={fence[1]} code={body.join('\n')} />);
			continue;
		}
		if (!line.trim()) { i += 1; continue; }
		const h = line.match(/^(#{1,6})\s+(.*)/);
		if (h) {
			const Tag = `h${Math.min(h[1].length + 2, 6)}`;
			blocks.push(<Tag key={key} className={styles.mdH}>{inline(h[2], key)}</Tag>);
			i += 1;
			continue;
		}
		if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) { blocks.push(<hr key={key} />); i += 1; continue; }
		if (line.includes('|') && isTableSep(lines[i + 1])) {
			const head = splitRow(line);
			const rows = [];
			i += 2;
			while (i < lines.length && lines[i].includes('|') && lines[i].trim()) rows.push(splitRow(lines[i++]));
			blocks.push(
				<div key={key} className={styles.mdTableWrap}>
					<table className={styles.mdTable}>
						<thead><tr>{head.map((c, j) => <th key={j}>{inline(c, `${key}h${j}`)}</th>)}</tr></thead>
						<tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, j) => <td key={j}>{inline(c, `${key}r${ri}c${j}`)}</td>)}</tr>)}</tbody>
					</table>
				</div>,
			);
			continue;
		}
		if (/^\s*>/.test(line)) {
			const body = [];
			while (i < lines.length && /^\s*>/.test(lines[i])) body.push(lines[i++].replace(/^\s*>\s?/, ''));
			blocks.push(<blockquote key={key} className={styles.mdQuote}><MarkdownView text={body.join('\n')} /></blockquote>);
			continue;
		}
		const listRe = /^\s*([-*+]|\d+[.)])\s+(.*)/;
		if (listRe.test(line)) {
			const ordered = /^\s*\d/.test(line);
			const items = [];
			while (i < lines.length && listRe.test(lines[i])) {
				const indent = lines[i].match(/^\s*/)[0].length;
				items.push({ indent, text: lines[i].match(listRe)[2] });
				i += 1;
			}
			const Tag = ordered ? 'ol' : 'ul';
			blocks.push(
				<Tag key={key} className={styles.mdList}>
					{items.map((it, j) => <li key={j} style={{ marginLeft: it.indent * 6 }}>{inline(it.text, `${key}l${j}`)}</li>)}
				</Tag>,
			);
			continue;
		}
		const para = [];
		while (i < lines.length && lines[i].trim() && !/^\s*(```|#{1,6}\s|>|[-*+]\s|\d+[.)]\s)/.test(lines[i])
			&& !(lines[i].includes('|') && isTableSep(lines[i + 1]))) para.push(lines[i++]);
		if (!para.length) para.push(lines[i++]);
		blocks.push(
			<p key={key} className={styles.mdP}>
				{para.map((p, j) => <span key={j}>{j > 0 && <br />}{inline(p, `${key}p${j}`)}</span>)}
			</p>,
		);
	}
	return <div className={styles.md}>{blocks}</div>;
}
